// Added By Farhan Khan Khan on 24-Dec-2025
import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Card,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import solisPowerApi from "../../api/solisPowerApi";

const BomSection = ({ sections, setSections, productOptions, brandOptions }) => {
  const [showAddSection, setShowAddSection] = useState(false);
  const [selectedCategoryForSection, setSelectedCategoryForSection] = useState("");
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [currentSectionForItem, setCurrentSectionForItem] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [bomCategoryOptions, setBomCategoryOptions] = useState([]);

  useEffect(() => {
    fetchBomCategories();
  }, []);

  const fetchBomCategories = async () => {
    const categories = await solisPowerApi.fetchActiveBomCategories();
    if (categories) {
      const options = categories.map(cat => ({
        value: cat.id,
        label: cat.name,
        code: cat.name.toUpperCase().replace(/\s+/g, '_')
      }));
      setBomCategoryOptions(options);
    }
  };

  const addNewSection = () => {
    if (!selectedCategoryForSection) return;

    const category = bomCategoryOptions.find(c => c.value === selectedCategoryForSection);
    if (!category) return;

    const newSection = {
      id: `section_${Date.now()}`,
      categoryId: category.value,
      categoryName: category.label,
      displayNumber: (sections.length + 1).toString(),
      isExpanded: true,
      items: []
    };

    setSections([...sections, newSection]);
    setShowAddSection(false);
    setSelectedCategoryForSection("");
  };

  const deleteSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
  };

  const toggleSection = (sectionId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, isExpanded: !section.isExpanded }
        : section
    ));
  };

  const openAddItemModal = (section) => {
    setCurrentSectionForItem(section);
    setShowAddItemModal(true);
    setSelectedProduct(null);
  };

  const addItemToSection = async () => {
    if (!selectedProduct || !currentSectionForItem) return;

    const product = productOptions.find(p => p.value === selectedProduct.value);
    if (!product) return;

    // Fetch product details for auto-fill
    let description = "";
    let brandIds = [];
    const toBrandIdsArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
    try {
      const productDetails = await solisPowerApi.fetchProductDetailsForBom(selectedProduct.value);
      if (productDetails) {
        description = productDetails.description || "";
        brandIds = toBrandIdsArr(productDetails.brand_id);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }

    const newItem = {
      id: `item_${Date.now()}`,
      product_id: selectedProduct.value,
      product_name: selectedProduct.label,
      product_description: description,
      quantity: 1,
      unit_price: "",
      discount: 0,
      brand_id: brandIds,
      unit: "Nos"
    };

    setSections(sections.map(section =>
      section.id === currentSectionForItem.id
        ? { ...section, items: [...section.items, newItem] }
        : section
    ));

    setShowAddItemModal(false);
    setSelectedProduct(null);
  };

  const deleteItem = (sectionId, itemId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, items: section.items.filter(i => i.id !== itemId) }
        : section
    ));
  };

  const updateItem = (sectionId, itemId, field, value) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? {
          ...section,
          items: section.items.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
          )
        }
        : section
    ));
  };

  const unitOptions = [
    { value: "Nos", label: "Nos" },
    { value: "Set", label: "Set" },
    { value: "Mtr", label: "Mtr" },
    { value: "SqM", label: "SqM" },
    { value: "Kg", label: "Kg" },
    { value: "KWp", label: "KWp" },
    { value: "KWh", label: "KWh" },
    { value: "Box", label: "Box" },
    { value: "Lot", label: "Lot" }
  ];

  return (
    <div className="mt-4">
      {/* BOM Header with Add Section Button */}
      <Row className="mb-3">
        <Col lg={8}>
          <h5>Bill of Materials</h5>
          <p className="text-muted small">Manage BOM sections and items for quotation</p>
        </Col>
        <Col lg={4} className="text-end">
          <Button
            size="sm"
            className="btn-create"
            onClick={() => setShowAddSection(true)}
          >
            <i className="fa fa-plus"></i> Add Section
          </Button>
        </Col>
      </Row>

      {/* BOM Sections */}
      {sections.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <i className="fa fa-box fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No BOM sections yet</h6>
            <p className="text-muted small mb-3">Start by adding your first section</p>
            <Button
              size="sm"
              className="btn-create"
              onClick={() => setShowAddSection(true)}
            >
              <i className="fa fa-plus"></i> Add First Section
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Accordion defaultActiveKey="0">
          {sections.map((section, sectionIndex) => (
            <Accordion.Item
              key={section.id}
              eventKey={sectionIndex.toString()}
            >
              <Accordion.Header>
                <div className="d-flex align-items-center w-100">
                  <span className="badge bg-custom me-2">{section.displayNumber}</span>
                  <span className="fw-bold">{section.categoryName}</span>
                  <span className="text-muted ms-2 small">({section.items.length} items)</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="d-flex justify-content-end mb-2">
                  <Button
                    size="sm"
                    variant="success"
                    className="me-2"
                    onClick={() => openAddItemModal(section)}
                  >
                    <i className="fa fa-plus"></i> Add Item
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => deleteSection(section.id)}
                  >
                    <i className="fa fa-trash"></i>
                  </Button>
                </div>

                {section.items.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fa fa-box fa-2x mb-2"></i>
                    <p>No items in this section</p>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => openAddItemModal(section)}
                    >
                      Add your first item
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: "5%" }}>#</th>
                          <th style={{ width: "25%" }}>Product</th>
                          <th style={{ width: "30%" }}>Description</th>
                          <th style={{ width: "15%" }}>Brand</th>
                          <th style={{ width: "12%" }}>Quantity</th>
                          <th style={{ width: "10%" }}>Unit</th>
                          <th style={{ width: "3%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.items.map((item, itemIndex) => (
                          <tr key={item.id}>
                            <td className="text-center">
                              <span className="badge bg-secondary">
                                {section.displayNumber}.{itemIndex + 1}
                              </span>
                            </td>
                            <td>
                              <Select
                                placeholder="Select Product..."
                                onChange={(selected) => {
                                  if (selected) {
                                    updateItem(section.id, item.id, 'product_id', selected.value);
                                    updateItem(section.id, item.id, 'product_name', selected.label);
                                    // Auto-fill description and brand
                                    const toArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
                                    solisPowerApi.fetchProductDetailsForBom(selected.value).then(details => {
                                      if (details) {
                                        updateItem(section.id, item.id, 'product_description', details.description || '');
                                        updateItem(section.id, item.id, 'brand_id', toArr(details.brand_id));
                                      }
                                    });
                                  }
                                }}
                                options={productOptions}
                                value={productOptions.find(p => p.value === item.product_id)}
                                isClearable
                                styles={{ control: (base) => ({ ...base, minHeight: "31px", height: "31px" }) }}
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                as="textarea"
                                rows={2}
                                value={item.product_description || ""}
                                onChange={(e) => updateItem(section.id, item.id, 'product_description', e.target.value)}
                                placeholder="Description"
                              />
                            </td>
                            <td>
                              <Select
                                placeholder="Select..."
                                onChange={(selected) => {
                                  const arr = selected && Array.isArray(selected) ? selected : [];
                                  updateItem(section.id, item.id, 'brand_id', arr.map(s => s.value));
                                }}
                                options={brandOptions}
                                value={brandOptions.filter(b => (Array.isArray(item.brand_id) ? item.brand_id : (item.brand_id ? [item.brand_id] : [])).includes(b.value))}
                                isMulti
                                isClearable
                                styles={{ control: (base) => ({ ...base, minHeight: "31px", height: "31px" }) }}
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                value={item.quantity || ""}
                                onChange={(e) => updateItem(section.id, item.id, 'quantity', e.target.value)}
                                placeholder="Qty (textual allowed)"
                              />
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={item.unit || "Nos"}
                                onChange={(e) => updateItem(section.id, item.id, 'unit', e.target.value)}
                              >
                                {unitOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Select>
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => deleteItem(section.id, item.id)}
                              >
                                <i className="fa fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* Add Section Modal */}
      <Modal show={showAddSection} onHide={() => setShowAddSection(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Select Category</Form.Label>
          <Select
            placeholder="Choose Category..."
            onChange={(selected) => setSelectedCategoryForSection(selected ? selected.value : "")}
            options={bomCategoryOptions}
            value={bomCategoryOptions.find(c => c.value === selectedCategoryForSection)}
            isClearable
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddSection(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={addNewSection}
            disabled={!selectedCategoryForSection}
          >
            Add Section
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Item Modal */}
      <Modal
        show={showAddItemModal}
        onHide={() => setShowAddItemModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Item to Section</Modal.Title>
          {currentSectionForItem && (
            <span className="ms-2 text-muted">{currentSectionForItem.categoryName}</span>
          )}
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Select Product</Form.Label>
          <Select
            placeholder="Select Product..."
            onChange={setSelectedProduct}
            options={productOptions}
            value={selectedProduct}
            isClearable
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddItemModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={addItemToSection}
            disabled={!selectedProduct}
          >
            Add Item
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BomSection;

